import type {
  CallbackDataParams,
  PiecewiseVisualMapOption
} from 'echarts/types/dist/shared.js'

import { IContinuousRoute } from '$/types'
import { useAppSelector } from 'app/hooks'
import Echart from 'common/components/Echart'
import useGetCurrentLine from 'common/hooks/getCurrentLine'
import {
  formatWithCommasOrExponential,
  getChartLabelBasedOnRiskUnit,
  lrChartTooltipFormatter,
  makeRUoptions
} from 'common/utils/chart/chartUtils'

import GraphContainerCard from 'features/RiskManagement/Components/Graphs/Components/GraphContainerCard'
import { selectRiskUnit } from 'features/RiskManagement/Components/LocalFilter/localFilterSelectors'

import tailwind from 'styles/themeTokens'

export interface IThreshold {
  id: number
  minValue: number
  maxValue: number
  colour: string
  code: string
  name: string
}

// We differentiate null from undefined as undefined representing
// gaps in the line segments from local filters whereas null represents missing
// data from the api call but the line segment still exists.
type LinerReferenceDataType = number | null | undefined

interface ILinearReferenceChart {
  showLoading: boolean
  XValues: number[]
  s1Values: LinerReferenceDataType[]
  s2Values: LinerReferenceDataType[]
  s1Label: string
  s2Label: string
  YBarValues1: number[]
  YBarValues2: number[]
  XLabel?: string
  YLabel?: string
  threshold: IThreshold[]
  measureValues: { fromMeasure: number; toMeasure: number }[]
  getLocalFilterRoutes: (currentLine: string) => IContinuousRoute[]
}

const LinearReferenceChart = ({
  showLoading,
  XValues,
  s1Values,
  s2Values,
  YBarValues1,
  YBarValues2,
  XLabel,
  s1Label,
  s2Label,
  YLabel,
  threshold,
  measureValues,
  getLocalFilterRoutes
}: ILinearReferenceChart) => {
  // If user selected a range, we need to show at least the range specified
  const adjustedXValues = XValues.slice()
  const adjustedMeasureValues = measureValues.slice()
  const adjustedS1Values = s1Values.slice()
  const adjustedS2Values = s2Values.slice()
  const adjustedYBarValues1 = YBarValues1?.slice() ?? []
  const adjustedYBarValues2 = YBarValues2?.slice() ?? []

  const currentLine = useGetCurrentLine() || ''
  const localFilterRoutes = getLocalFilterRoutes(currentLine)

  if (localFilterRoutes.length > 0) {
    if (localFilterRoutes[0].fromMeasure < adjustedXValues[0]) {
      adjustedMeasureValues.unshift({
        fromMeasure: localFilterRoutes[0].fromMeasure,
        toMeasure: adjustedXValues[0]
      })
      adjustedXValues.unshift(localFilterRoutes[0].fromMeasure)
      adjustedS1Values?.unshift(undefined)
      adjustedYBarValues1.unshift(-1)

      if (adjustedS2Values.length > 0) {
        adjustedS2Values.unshift(undefined)
        adjustedYBarValues2.unshift(-1)
      }
    }

    if (
      localFilterRoutes[localFilterRoutes.length - 1].toMeasure >
      adjustedXValues[adjustedXValues.length - 1]
    ) {
      adjustedMeasureValues.push({
        fromMeasure: adjustedXValues[adjustedXValues.length - 1],
        toMeasure: localFilterRoutes[localFilterRoutes.length - 1].toMeasure
      })

      adjustedMeasureValues.push({
        fromMeasure: adjustedXValues[adjustedXValues.length - 1],
        toMeasure: localFilterRoutes[localFilterRoutes.length - 1].toMeasure
      })

      adjustedXValues.push(
        localFilterRoutes[localFilterRoutes.length - 1].toMeasure
      )
      adjustedS1Values.push(undefined)
      adjustedYBarValues1.push(-1)

      if (adjustedS2Values.length > 0) {
        adjustedS2Values.push(undefined)
        adjustedYBarValues2.push(-1)
      }
    }
  }

  for (let i = 0; i < adjustedS1Values.length; ++i) {
    if (adjustedS1Values[i] === -1) {
      adjustedS1Values[i] = undefined
    }
  }

  for (let i = 0; i < adjustedS2Values.length; ++i) {
    if (adjustedS2Values[i] === -1) {
      adjustedS2Values[i] = undefined
    }
  }

  //  assignees: JANA-itsaishvili
  const riskUnit = useAppSelector(selectRiskUnit)

  const line1Data = adjustedS1Values.map((val, index) => {
    return {
      value: [adjustedXValues[index], val],
      visualMap: false,
      fromMeasure: adjustedMeasureValues[index]?.fromMeasure ?? 0,
      toMeasure: adjustedMeasureValues[index]?.toMeasure ?? 0
    }
  })

  const line2Data = adjustedS2Values.map(
    (val: LinerReferenceDataType, index: number) => {
      return {
        value: [adjustedXValues[index], val],
        visualMap: false,
        fromMeasure: adjustedMeasureValues[index]?.fromMeasure ?? 0,
        toMeasure: adjustedMeasureValues[index]?.toMeasure ?? 0
      }
    }
  )

  // Since Values are pointed on fromMeasures above, it is missing the plotting for last segment's toMeasure
  if (line1Data.length > 0) {
    line1Data.push({
      value: [
        adjustedXValues[adjustedXValues.length - 1],
        adjustedS1Values[adjustedS1Values.length - 1]
      ],
      visualMap: false,
      fromMeasure: adjustedXValues[adjustedXValues.length - 2],
      toMeasure: adjustedXValues[adjustedXValues.length - 1]
    })
  }

  if (line2Data.length > 0) {
    line2Data.push({
      value: [
        adjustedXValues[adjustedXValues.length - 1],
        adjustedS2Values[adjustedS2Values.length - 1]
      ],
      visualMap: false,
      fromMeasure: adjustedXValues[adjustedXValues.length - 2],
      toMeasure: adjustedXValues[adjustedXValues.length - 1]
    })
  }

  const bar1Data: number[][] = []
  const bar2Data: number[][] = []
  const legendData: string[][] = []
  let XIndex = adjustedXValues[0]

  adjustedXValues.slice(1).forEach((value, YIndex) => {
    for (XIndex; XIndex < value; XIndex++) {
      bar1Data.push([
        XIndex - adjustedXValues[0],
        0,
        adjustedYBarValues1[YIndex]
      ])

      bar2Data.push([
        XIndex - adjustedXValues[0],
        0,
        adjustedYBarValues2[YIndex]
      ])
      legendData.push([(XIndex - adjustedXValues[0]).toFixed(3)])
    }

    YIndex++
  })

  const heatmapColorConfig: PiecewiseVisualMapOption['pieces'] = []

  threshold.forEach((i: IThreshold) => {
    heatmapColorConfig.push({
      gt: i.minValue,
      lte: i.maxValue,
      color: '#' + i.colour
    })
  })

  const formatterOptions = makeRUoptions('ru')

  const formatLabel = (label: string) => {
    return label.length > 8 ? label.substring(0, 8) + '...' : label
  }

  const color =
    adjustedS2Values.length === 0
      ? tailwind.theme.colors.lighthouseBlue.DEFAULT
      : tailwind.theme.colors.gray[700]

  const options = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      padding: 12,
      formatter: (params: CallbackDataParams[]) =>
        lrChartTooltipFormatter(params, undefined, true, currentLine)
    },
    visualMap: {
      show: false,
      pieces: heatmapColorConfig,
      outOfRange: {
        color: tailwind.theme.colors.white
      }
    },
    grid: [
      {
        top: 62,
        left: 80,
        right: 32,
        bottom: 48
      },
      {
        top: 0,
        left: 80,
        right: 32,
        height: 14
      },
      {
        top: 22,
        left: 80,
        right: 32,
        height: 14
      }
    ],
    axisPointer: {
      link: { xAxisIndex: 'all' }
    },
    xAxis: [
      {
        name: XLabel ?? 'Distance (ft)',
        nameLocation: 'middle',
        nameGap: 32,
        nameTextStyle: {
          color: tailwind.theme.colors.black,
          fontWeight: 'bold'
        },
        type: 'value',
        max: 'dataMax',
        min: 'dataMin',
        axisTick: { show: false },
        axisLabel: {
          formatter: (value: string | number) =>
            formatWithCommasOrExponential(value, {
              isMeasure: true,
              isGraph: true
            })
        }
      },
      {
        gridIndex: 1,
        data: legendData,
        scale: true,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false }
      },
      {
        gridIndex: 2,
        data: legendData,
        scale: true,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false }
      }
    ],
    yAxis: [
      {
        scale: true,
        name: YLabel ?? getChartLabelBasedOnRiskUnit(riskUnit),
        nameRotate: 90,
        nameLocation: 'middle',
        nameGap: 64,
        nameTextStyle: {
          color: tailwind.theme.colors.black,
          fontWeight: 'bold'
        },
        axisLine: {
          show: true
        },
        axisLabel: {
          formatter: (value: string | number) =>
            formatWithCommasOrExponential(value, formatterOptions)
        }
      },
      {
        data: [s1Label ? formatLabel(s1Label) : ''],
        scale: true,
        gridIndex: 1,
        splitNumber: 2,
        axisLabel: { show: true },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false }
      },
      {
        data: [s2Values.length === 0 ? '' : formatLabel(s2Label)],
        scale: true,
        gridIndex: 2,
        splitNumber: 2,
        axisLabel: { show: true },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false }
      }
    ],
    series: [
      {
        type: 'line',
        step: 'end',
        data: line1Data,
        name: s1Label,
        symbol: 'none',
        color,
        lineStyle: {
          color
        },
        tooltip: {
          show: true
        }
      },
      {
        type: 'line',
        step: 'end',
        data: line2Data,
        name: s2Label,
        symbol: 'none',
        color: tailwind.theme.colors.lighthouseBlue.DEFAULT,
        lineStyle: {
          color: tailwind.theme.colors.lighthouseBlue.DEFAULT
        },
        tooltip: {
          show: true
        }
      },
      {
        name: 'baseline',
        type: 'heatmap',
        xAxisIndex: 1,
        yAxisIndex: 1,
        data: bar1Data,
        tooltip: {
          show: false
        },
        progressive: 0
      },
      {
        name: 'wis',
        type: 'heatmap',
        xAxisIndex: 2,
        yAxisIndex: 2,
        data: bar2Data,
        tooltip: {
          show: false
        },
        progressive: 0
      }
    ]
  }

  return (
    <GraphContainerCard title='Linear Reference'>
      <Echart option={options} showLoading={showLoading} />
    </GraphContainerCard>
  )
}

export default LinearReferenceChart
